import { useEffect, useRef, useState } from "react";
import { stopAudio } from "./audio";

// Microphone recorder: start/stop with a safety timeout; returns a local clip URL.
export const useMic = ({ maxMs = 8000, onRecorded, autoPlay = true } = {}) => {
  const [state, setState] = useState("idle");
  const [clip, setClip] = useState(null);
  const [noMic, setNoMic] = useState(false);
  const mr = useRef(null);
  const timer = useRef(null);
  const alive = useRef(true);
  const finalize = useRef(null);
  const clipRef = useRef(null);
  const cb = useRef(onRecorded);
  cb.current = onRecorded;
  clipRef.current = clip;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (clipRef.current) URL.revokeObjectURL(clipRef.current);
      clearTimeout(timer.current);
      if (mr.current?.state === "recording") mr.current.stop();
    };
  }, []);

  const stop = () => {
    clearTimeout(timer.current);
    setState((s) => (s === "recording" ? "stopping" : s));
    const rec = mr.current;
    try { if (rec && rec.state !== "inactive") { rec.requestData?.(); rec.stop(); } } catch { /* already stopped */ }
    const fin = finalize.current;
    setTimeout(() => fin?.(), 600);
  };
  const start = async () => {
    stopAudio();
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setNoMic(true);
      setState("done");
      return;
    }
    const rec = new MediaRecorder(stream);
    const chunks = [];
    let ended = false;
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    const end = () => {
      if (ended) return;
      ended = true;
      finalize.current = null;
      stream.getTracks().forEach((t) => t.stop());
      clearTimeout(timer.current);
      if (!alive.current) return;
      const url = chunks.length ? URL.createObjectURL(new Blob(chunks, { type: rec.mimeType || "audio/webm" })) : null;
      setClip((old) => { if (old) URL.revokeObjectURL(old); return url; });
      setState("done");
      if (url) {
        cb.current?.();
        if (autoPlay) new Audio(url).play().catch(() => {});
      }
    };
    rec.onstop = end;
    finalize.current = end;
    mr.current = rec;
    rec.start(250);
    setState("recording");
    timer.current = setTimeout(stop, maxMs);
  };
  return { state, clip, noMic, start, stop };
};
